import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  Key,
  ShoppingCart,
  Monitor,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  Loader2,
  Eye,
  Copy,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  RotateCcw,
  Info,
  Search,
  User,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Product, License, Order, LicenseActivation } from "@/lib/license-service";
import { completeOrder, resetLicense } from "@/lib/license-service";
import { toast } from "sonner";

type ProfileSummary = {
  display_name: string | null;
  email: string | null;
};

const LicenseManagement = () => {
  const [activeTab, setActiveTab] = useState("products");
  const [loading, setLoading] = useState(true);

  // Products state
  const [products, setProducts] = useState<Product[]>([]);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    version: "",
    download_url: "",
    price: 0,
    max_activations: 2,
    is_active: true,
  });

  // Licenses state
  const [licenses, setLicenses] = useState<License[]>([]);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);

  // Orders state
  const [orders, setOrders] = useState<Order[]>([]);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Activations state
  const [activations, setActivations] = useState<LicenseActivation[]>([]);
  const [allActivations, setAllActivations] = useState<any[]>([]);
  const [showActivationsDialog, setShowActivationsDialog] = useState(false);
  const [resettingLicenseId, setResettingLicenseId] = useState<string | null>(null);
  const [showApiDialog, setShowApiDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [profilesByUserId, setProfilesByUserId] = useState<Record<string, ProfileSummary>>({});
  const [ownerDialogLicense, setOwnerDialogLicense] = useState<License | null>(null);
  const [showOwnerDialog, setShowOwnerDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === "products") {
        await loadProducts();
      } else if (activeTab === "licenses") {
        await loadLicenses();
      } else if (activeTab === "orders") {
        await loadOrders();
      } else if (activeTab === "activations") {
        await loadAllActivations();
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  const loadAllActivations = async () => {
    const { data, error } = await supabase
      .from("license_activations")
      .select(`
        *,
        licenses (
          license_key,
          status,
          products (name)
        )
      `)
      .order("last_validated_at", { ascending: false });

    if (error) throw error;
    setAllActivations(data || []);
  };

  const loadProducts = async () => {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw error;
    setProducts(data || []);
  };

  const fetchProfilesForUserIds = async (userIds: string[]) => {
    const uniqueIds = [...new Set(userIds.filter(Boolean))];
    if (uniqueIds.length === 0) return;

    const { data, error } = await supabase
      .from("profiles")
      .select("user_id, display_name, email")
      .in("user_id", uniqueIds);

    if (error) throw error;
    if (!data) return;

    const profilesMap = data.reduce((acc, profile) => {
      acc[profile.user_id] = {
        display_name: profile.display_name,
        email: profile.email,
      };
      return acc;
    }, {} as Record<string, ProfileSummary>);

    setProfilesByUserId((prev) => ({ ...prev, ...profilesMap }));
  };

  const getProfileSummary = (userId: string) => {
    const profile = profilesByUserId[userId];
    if (!profile) {
      return { primary: "Unknown", secondary: userId };
    }

    const primary = profile.display_name || profile.email || "Unknown";
    const secondary = profile.display_name && profile.email ? profile.email : userId;
    return { primary, secondary };
  };

  const loadLicenses = async () => {
    const { data, error } = await supabase
      .from("licenses")
      .select(`
        *,
        products (*)
      `)
      .order("created_at", { ascending: false });

    if (error) throw error;
    const licensesData = (data || []) as License[];
    setLicenses(licensesData);
    await fetchProfilesForUserIds(licensesData.map((license) => license.user_id));
  };

  const loadOrders = async () => {
    const { data, error } = await supabase
      .from("orders")
      .select(`
        *,
        products (*),
        licenses (*)
      `)
      .order("created_at", { ascending: false });

    if (error) throw error;
    const ordersData = (data || []) as Order[];
    setOrders(ordersData);
    await fetchProfilesForUserIds(ordersData.map((order) => order.user_id));
  };

  const handleSaveProduct = async () => {
    try {
      if (editingProduct) {
        // Update existing product
        const { error } = await supabase
          .from("products")
          .update(productForm)
          .eq("id", editingProduct.id);

        if (error) throw error;
        toast.success("อัพเดทสินค้าสำเร็จ");
      } else {
        // Create new product
        const { error } = await supabase
          .from("products")
          .insert([productForm]);

        if (error) throw error;
        toast.success("เพิ่มสินค้าสำเร็จ");
      }

      setShowProductDialog(false);
      setEditingProduct(null);
      setProductForm({
        name: "",
        description: "",
        version: "",
        download_url: "",
        price: 0,
        max_activations: 2,
        is_active: true,
      });
      loadProducts();
    } catch (error: any) {
      console.error("Error saving product:", error);
      toast.error("เกิดข้อผิดพลาด: " + error.message);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!confirm("คุณแน่ใจหรือไม่ว่าต้องการลบสินค้านี้?")) return;

    try {
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("ลบสินค้าสำเร็จ");
      loadProducts();
    } catch (error: any) {
      console.error("Error deleting product:", error);
      toast.error("เกิดข้อผิดพลาด: " + error.message);
    }
  };

  const handleApproveOrder = async (order: Order) => {
    try {
      const result = await completeOrder(order.id);

      if (result.success) {
        toast.success("อนุมัติคำสั่งซื้อและสร้าง License สำเร็จ");
        loadOrders();
      } else {
        toast.error("เกิดข้อผิดพลาด: " + result.error);
      }
    } catch (error: any) {
      console.error("Error approving order:", error);
      toast.error("เกิดข้อผิดพลาด: " + error.message);
    }
  };

  const handleRejectOrder = async (orderId: string) => {
    if (!confirm("คุณแน่ใจหรือไม่ว่าต้องการปฏิเสธคำสั่งซื้อนี้?")) return;

    try {
      const { error } = await supabase
        .from("orders")
        .update({ status: "failed" })
        .eq("id", orderId);

      if (error) throw error;
      toast.success("ปฏิเสธคำสั่งซื้อสำเร็จ");
      loadOrders();
    } catch (error: any) {
      console.error("Error rejecting order:", error);
      toast.error("เกิดข้อผิดพลาด: " + error.message);
    }
  };

  const handleViewOrderDetails = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDialog(true);
  };

  const handleViewOwner = (license: License) => {
    setOwnerDialogLicense(license);
    setShowOwnerDialog(true);
  };

  const handleViewActivations = async (license: License) => {
    setSelectedLicense(license);
    try {
      const { data, error } = await supabase
        .from("license_activations")
        .select("*")
        .eq("license_id", license.id)
        .order("activated_at", { ascending: false });

      if (error) throw error;
      setActivations(data || []);
      setShowActivationsDialog(true);
    } catch (error: any) {
      console.error("Error loading activations:", error);
      toast.error("เกิดข้อผิดพลาดในการโหลดข้อมูล Activation");
    }
  };

  const handleUpdateLicenseStatus = async (licenseId: string, status: 'active' | 'suspended' | 'revoked') => {
    try {
      const { error } = await supabase
        .from("licenses")
        .update({ status })
        .eq("id", licenseId);

      if (error) throw error;
      toast.success("อัพเดทสถานะ License สำเร็จ");
      loadLicenses();
    } catch (error: any) {
      console.error("Error updating license status:", error);
      toast.error("เกิดข้อผิดพลาด: " + error.message);
    }
  };

  const handleResetMachines = async (licenseKey: string, licenseId: string) => {
    if (!confirm("คุณแน่ใจหรือไม่? การรีเซ็ตจะลบเครื่องที่ลงทะเบียนทั้งหมด")) return;

    setResettingLicenseId(licenseId);
    try {
      const result = await resetLicense(licenseKey);
      if (result.success) {
        toast.success("รีเซ็ตเครื่องสำเร็จ");
        loadLicenses();
      } else {
        toast.error("เกิดข้อผิดพลาด: " + result.error);
      }
    } catch (error: any) {
      toast.error("เกิดข้อผิดพลาด: " + error.message);
    } finally {
      setResettingLicenseId(null);
    }
  };

  const handleDeactivateMachine = async (activationId: string) => {
    if (!confirm("คุณแน่ใจหรือไม่ว่าต้องการปิดการใช้งานเครื่องนี้?")) return;

    try {
      const { error } = await supabase
        .from("license_activations")
        .update({ is_active: false })
        .eq("id", activationId);

      if (error) throw error;
      toast.success("ปิดการใช้งานเครื่องสำเร็จ");

      // Decrease current_activations count
      const activation = allActivations.find(a => a.id === activationId);
      if (activation?.license_id) {
        await supabase.rpc('decrement_activation_count', {
          license_id_param: activation.license_id
        });
      }

      loadAllActivations();
    } catch (error: any) {
      toast.error("เกิดข้อผิดพลาด: " + error.message);
    }
  };

  const getApiEndpoint = () => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "https://your-project.supabase.co";
    return `${supabaseUrl}/rest/v1/rpc/validate_and_activate_license`;
  };

  const getAnonKey = () => {
    return import.meta.env.VITE_SUPABASE_ANON_KEY || "your-anon-key";
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("คัดลอกแล้ว");
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: any; icon: any; label: string }> = {
      active: { variant: "default", icon: CheckCircle, label: "ใช้งานได้" },
      suspended: { variant: "secondary", icon: Clock, label: "ระงับชั่วคราว" },
      revoked: { variant: "destructive", icon: XCircle, label: "ยกเลิก" },
      pending: { variant: "secondary", icon: Clock, label: "รอดำเนินการ" },
      completed: { variant: "default", icon: CheckCircle, label: "สำเร็จ" },
      failed: { variant: "destructive", icon: XCircle, label: "ล้มเหลว" },
      refunded: { variant: "outline", icon: RefreshCw, label: "คืนเงิน" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant as any} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="products" className="gap-2">
            <Package className="h-4 w-4" />
            สินค้า
          </TabsTrigger>
          <TabsTrigger value="licenses" className="gap-2">
            <Key className="h-4 w-4" />
            License
          </TabsTrigger>
          <TabsTrigger value="orders" className="gap-2">
            <ShoppingCart className="h-4 w-4" />
            คำสั่งซื้อ
          </TabsTrigger>
          <TabsTrigger value="activations" className="gap-2">
            <Monitor className="h-4 w-4" />
            Activations
          </TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-4">
          <Card variant="glass">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">จัดการสินค้า</h2>
                <p className="text-sm text-muted-foreground">จัดการสินค้าโปรแกรมที่ขาย</p>
              </div>
              <Button
                onClick={() => {
                  setEditingProduct(null);
                  setProductForm({
                    name: "",
                    description: "",
                    version: "",
                    download_url: "",
                    price: 0,
                    max_activations: 2,
                    is_active: true,
                  });
                  setShowProductDialog(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                เพิ่มสินค้า
              </Button>
            </div>
            <CardContent className="p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  ยังไม่มีสินค้า
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ชื่อสินค้า</TableHead>
                      <TableHead>เวอร์ชั่น</TableHead>
                      <TableHead>ราคา</TableHead>
                      <TableHead>Max Activations</TableHead>
                      <TableHead>สถานะ</TableHead>
                      <TableHead className="text-right">จัดการ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{product.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {product.description}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{product.version}</TableCell>
                        <TableCell>{product.price.toLocaleString()} บาท</TableCell>
                        <TableCell>{product.max_activations}</TableCell>
                        <TableCell>
                          {product.is_active ? (
                            <Badge variant="default">เปิดใช้งาน</Badge>
                          ) : (
                            <Badge variant="secondary">ปิดใช้งาน</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingProduct(product);
                                setProductForm(product);
                                setShowProductDialog(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Licenses Tab */}
        <TabsContent value="licenses" className="space-y-4">
          <Card variant="glass">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">License ทั้งหมด</h2>
                <p className="text-sm text-muted-foreground">จัดการ License ของผู้ใช้</p>
              </div>
              <Button variant="outline" onClick={() => setShowApiDialog(true)} className="gap-2">
                <Info className="h-4 w-4" />
                API Info
              </Button>
            </div>
            <CardContent className="p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : licenses.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  ยังไม่มี License
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>License Key</TableHead>
                      <TableHead>สินค้า</TableHead>
                      <TableHead>เจ้าของ</TableHead>
                      <TableHead>Activations</TableHead>
                      <TableHead>สถานะ</TableHead>
                      <TableHead>หมดอายุ</TableHead>
                      <TableHead className="text-right">จัดการ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {licenses.map((license) => (
                      <TableRow key={license.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="text-sm bg-muted px-2 py-1 rounded">
                              {license.license_key}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(license.license_key)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          {license.products ? (license.products as any).name : "N/A"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">
                                {getProfileSummary(license.user_id).primary}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {getProfileSummary(license.user_id).secondary}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewOwner(license)}
                              title="ดูเจ้าของ"
                            >
                              <User className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {license.current_activations} / {license.max_activations}
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(license.status)}</TableCell>
                        <TableCell>
                          {license.expires_at
                            ? new Date(license.expires_at).toLocaleDateString("th-TH")
                            : "ไม่มีกำหนด"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewActivations(license)}
                              title="ดู Activations"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResetMachines(license.license_key, license.id)}
                              disabled={resettingLicenseId === license.id || license.current_activations === 0}
                              title="รีเซ็ตเครื่อง"
                            >
                              {resettingLicenseId === license.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <RotateCcw className="h-4 w-4" />
                              )}
                            </Button>
                            <Select
                              value={license.status}
                              onValueChange={(value: any) =>
                                handleUpdateLicenseStatus(license.id, value)
                              }
                            >
                              <SelectTrigger className="w-32 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">ใช้งานได้</SelectItem>
                                <SelectItem value="suspended">ระงับ</SelectItem>
                                <SelectItem value="revoked">ยกเลิก</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <Card variant="glass">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">คำสั่งซื้อทั้งหมด</h2>
              <p className="text-sm text-muted-foreground">จัดการคำสั่งซื้อและการชำระเงิน</p>
            </div>
            <CardContent className="p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  ยังไม่มีคำสั่งซื้อ
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>วันที่</TableHead>
                      <TableHead>สินค้า</TableHead>
                      <TableHead>ผู้ซื้อ</TableHead>
                      <TableHead>ราคา</TableHead>
                      <TableHead>สถานะ</TableHead>
                      <TableHead>การชำระเงิน</TableHead>
                      <TableHead className="text-right">จัดการ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>
                          {new Date(order.created_at).toLocaleDateString("th-TH", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell>
                          {order.products ? (order.products as any).name : "N/A"}
                        </TableCell>
                        <TableCell>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {getProfileSummary(order.user_id).primary}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {getProfileSummary(order.user_id).secondary}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>{order.amount.toLocaleString()} บาท</TableCell>
                        <TableCell>{getStatusBadge(order.status)}</TableCell>
                        <TableCell>
                          {order.payment_proof_url ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(order.payment_proof_url!, "_blank")}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              ดูหลักฐาน
                            </Button>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              ยังไม่มีหลักฐาน
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewOrderDetails(order)}
                              title="ดูรายละเอียดผู้ซื้อ"
                            >
                              <User className="h-4 w-4" />
                            </Button>
                            {order.status === "pending" && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleApproveOrder(order)}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                อนุมัติ
                              </Button>
                            )}
                            {order.status === "pending" && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleRejectOrder(order.id)}
                              >
                                <X className="h-4 w-4 mr-1" />
                                ปฏิเสธ
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activations Summary Tab */}
        <TabsContent value="activations" className="space-y-4">
          <Card variant="glass">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Activation ทั้งหมด</h2>
                <p className="text-sm text-muted-foreground">
                  รายการเครื่องที่ลงทะเบียนทั้งหมดในระบบ ({allActivations.filter(a => a.is_active).length} active)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="ค้นหา License Key / Machine..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <Button variant="outline" size="icon" onClick={loadAllActivations}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <CardContent className="p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : allActivations.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  ยังไม่มี Activation ในระบบ
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>License Key</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Machine ID</TableHead>
                      <TableHead>Machine Name</TableHead>
                      <TableHead>OS</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Last Seen</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allActivations
                      .filter(a => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        return (
                          a.licenses?.license_key?.toLowerCase().includes(query) ||
                          a.machine_id?.toLowerCase().includes(query) ||
                          a.machine_name?.toLowerCase().includes(query)
                        );
                      })
                      .map((activation) => (
                      <TableRow key={activation.id} className={!activation.is_active ? "opacity-50" : ""}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {activation.licenses?.license_key || "N/A"}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(activation.licenses?.license_key || "")}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {activation.licenses?.products?.name || "N/A"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs">{activation.machine_id?.substring(0, 16)}...</code>
                        </TableCell>
                        <TableCell>{activation.machine_name || "N/A"}</TableCell>
                        <TableCell>
                          <span className="text-xs">{activation.os_info || "N/A"}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs">{activation.ip_address || "N/A"}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs">
                            {new Date(activation.last_validated_at).toLocaleDateString("th-TH", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </span>
                        </TableCell>
                        <TableCell>
                          {activation.is_active ? (
                            <Badge variant="default" className="gap-1">
                              <CheckCircle className="h-3 w-3" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <XCircle className="h-3 w-3" />
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {activation.is_active && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeactivateMachine(activation.id)}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Deactivate
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? "แก้ไขสินค้า" : "เพิ่มสินค้าใหม่"}
            </DialogTitle>
            <DialogDescription>
              กรอกข้อมูลสินค้าโปรแกรมที่ต้องการขาย
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">ชื่อสินค้า</Label>
              <Input
                id="name"
                value={productForm.name}
                onChange={(e) =>
                  setProductForm({ ...productForm, name: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">คำอธิบาย</Label>
              <Textarea
                id="description"
                value={productForm.description}
                onChange={(e) =>
                  setProductForm({ ...productForm, description: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="version">เวอร์ชั่น</Label>
                <Input
                  id="version"
                  value={productForm.version}
                  onChange={(e) =>
                    setProductForm({ ...productForm, version: e.target.value })
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="price">ราคา (บาท)</Label>
                <Input
                  id="price"
                  type="number"
                  value={productForm.price}
                  onChange={(e) =>
                    setProductForm({ ...productForm, price: parseInt(e.target.value) })
                  }
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="download_url">URL สำหรับดาวน์โหลด</Label>
              <Input
                id="download_url"
                value={productForm.download_url}
                onChange={(e) =>
                  setProductForm({ ...productForm, download_url: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="max_activations">จำนวน Activation สูงสุด</Label>
                <Input
                  id="max_activations"
                  type="number"
                  value={productForm.max_activations}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      max_activations: parseInt(e.target.value),
                    })
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="is_active">สถานะ</Label>
                <Select
                  value={productForm.is_active.toString()}
                  onValueChange={(value) =>
                    setProductForm({ ...productForm, is_active: value === "true" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">เปิดใช้งาน</SelectItem>
                    <SelectItem value="false">ปิดใช้งาน</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)}>
              ยกเลิก
            </Button>
            <Button onClick={handleSaveProduct}>บันทึก</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>รายละเอียดผู้ซื้อ</DialogTitle>
            <DialogDescription>
              คำสั่งซื้อ {selectedOrder?.id}
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">ผู้ซื้อ</span>
                <span className="col-span-2 font-medium">
                  {getProfileSummary(selectedOrder.user_id).primary}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">อีเมล</span>
                <span className="col-span-2">
                  {profilesByUserId[selectedOrder.user_id]?.email || "-"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">User ID</span>
                <code className="col-span-2 text-xs bg-muted px-2 py-1 rounded">
                  {selectedOrder.user_id}
                </code>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">สินค้า</span>
                <span className="col-span-2">
                  {selectedOrder.products ? (selectedOrder.products as any).name : "N/A"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">ราคา</span>
                <span className="col-span-2">{selectedOrder.amount.toLocaleString()} บาท</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">สถานะ</span>
                <span className="col-span-2">{getStatusBadge(selectedOrder.status)}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">ช่องทางชำระเงิน</span>
                <span className="col-span-2">{selectedOrder.payment_method || "-"}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">License Key</span>
                <span className="col-span-2">
                  {selectedOrder.licenses?.license_key || "-"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">วันที่สั่งซื้อ</span>
                <span className="col-span-2">
                  {new Date(selectedOrder.created_at).toLocaleDateString("th-TH", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">หลักฐาน</span>
                <span className="col-span-2">
                  {selectedOrder.payment_proof_url ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(selectedOrder.payment_proof_url!, "_blank")}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      ดูหลักฐาน
                    </Button>
                  ) : (
                    "-"
                  )}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Owner Dialog */}
      <Dialog open={showOwnerDialog} onOpenChange={setShowOwnerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>เจ้าของ License</DialogTitle>
            <DialogDescription>
              ข้อมูลผู้ใช้สำหรับ License ที่เลือก
            </DialogDescription>
          </DialogHeader>
          {ownerDialogLicense && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">License Key</span>
                <span className="col-span-2 font-medium">
                  {ownerDialogLicense.license_key}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">สินค้า</span>
                <span className="col-span-2">
                  {ownerDialogLicense.products ? (ownerDialogLicense.products as any).name : "N/A"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">ผู้ใช้</span>
                <span className="col-span-2 font-medium">
                  {getProfileSummary(ownerDialogLicense.user_id).primary}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">อีเมล</span>
                <span className="col-span-2">
                  {profilesByUserId[ownerDialogLicense.user_id]?.email || "-"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">User ID</span>
                <code className="col-span-2 text-xs bg-muted px-2 py-1 rounded">
                  {ownerDialogLicense.user_id}
                </code>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">สถานะ</span>
                <span className="col-span-2">{getStatusBadge(ownerDialogLicense.status)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Activations Dialog */}
      <Dialog open={showActivationsDialog} onOpenChange={setShowActivationsDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Activation Details</DialogTitle>
            <DialogDescription>
              รายการเครื่องที่เปิดใช้งาน License:{" "}
              {selectedLicense?.license_key}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {activations.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                ยังไม่มีการ Activate
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Machine ID</TableHead>
                    <TableHead>Machine Name</TableHead>
                    <TableHead>OS</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Activated</TableHead>
                    <TableHead>Last Validated</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activations.map((activation) => (
                    <TableRow key={activation.id}>
                      <TableCell>
                        <code className="text-xs">{activation.machine_id}</code>
                      </TableCell>
                      <TableCell>{activation.machine_name || "N/A"}</TableCell>
                      <TableCell>{activation.os_info || "N/A"}</TableCell>
                      <TableCell>{activation.ip_address || "N/A"}</TableCell>
                      <TableCell>
                        {new Date(activation.activated_at).toLocaleDateString("th-TH")}
                      </TableCell>
                      <TableCell>
                        {new Date(activation.last_validated_at).toLocaleDateString(
                          "th-TH"
                        )}
                      </TableCell>
                      <TableCell>
                        {activation.is_active ? (
                          <Badge variant="default">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* API Info Dialog */}
      <Dialog open={showApiDialog} onOpenChange={setShowApiDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>License Validation API</DialogTitle>
            <DialogDescription>
              ใช้ API นี้ในโปรแกรม Desktop เพื่อตรวจสอบ License Key
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Endpoint URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 p-2 bg-muted rounded text-xs overflow-x-auto">
                  {getApiEndpoint()}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(getApiEndpoint())}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Method</Label>
              <div className="mt-1">
                <Badge>POST</Badge>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Headers</Label>
              <pre className="mt-1 p-3 bg-muted rounded text-xs overflow-x-auto">
{`{
  "apikey": "${getAnonKey().substring(0, 30)}...",
  "Content-Type": "application/json"
}`}
              </pre>
            </div>

            <div>
              <Label className="text-sm font-medium">Request Body</Label>
              <pre className="mt-1 p-3 bg-muted rounded text-xs overflow-x-auto">
{`{
  "license_key_param": "XXXX-XXXX-XXXX-XXXX",
  "machine_id_param": "<hardware-fingerprint>",
  "machine_name_param": "My Computer",
  "ip_address_param": "192.168.1.100",
  "os_info_param": "Windows 11"
}`}
              </pre>
            </div>

            <div>
              <Label className="text-sm font-medium text-green-600">Response (Success)</Label>
              <pre className="mt-1 p-3 bg-green-500/10 border border-green-500/30 rounded text-xs overflow-x-auto">
{`{
  "success": true,
  "message": "License activated successfully",
  "activationsUsed": 1,
  "maxActivations": 2
}`}
              </pre>
            </div>

            <div>
              <Label className="text-sm font-medium text-red-600">Response (Error - Max Reached)</Label>
              <pre className="mt-1 p-3 bg-red-500/10 border border-red-500/30 rounded text-xs overflow-x-auto">
{`{
  "success": false,
  "error": "Maximum activations reached (2/2). Please reset from dashboard.",
  "activationsUsed": 2,
  "maxActivations": 2
}`}
              </pre>
            </div>

            <div>
              <Label className="text-sm font-medium text-red-600">Response (Error - Invalid Key)</Label>
              <pre className="mt-1 p-3 bg-red-500/10 border border-red-500/30 rounded text-xs overflow-x-auto">
{`{
  "success": false,
  "error": "Invalid license key"
}`}
              </pre>
            </div>

            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded">
              <h4 className="font-medium text-yellow-600 mb-2">Implementation Notes</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• โปรแกรมต้องเรียก API นี้ทุกครั้งที่เปิด</li>
                <li>• ถ้าไม่มี Internet จะใช้งานไม่ได้</li>
                <li>• Machine ID ควรเป็น Hardware Fingerprint ที่ไม่เปลี่ยน</li>
                <li>• ถ้าเปลี่ยนเครื่อง user ต้องมา Reset จาก Dashboard</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowApiDialog(false)}>ปิด</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LicenseManagement;
